import { hourMs, minuteMs, secondMs } from "verdi-common/timeConstants";

/** The API's vocabulary — every key is a valid `bucketWidth` on the wire. */
export const BUCKET_WIDTH_MS = {
    "1s": secondMs,
    "5s": 5 * secondMs,
    "15s": 15 * secondMs,
    "30s": 30 * secondMs,
    "1m": minuteMs,
    "5m": 5 * minuteMs,
    "1h": hourMs,
} as const;

export type BucketWidth = keyof typeof BUCKET_WIDTH_MS;

/** What a panel resolves to, and how the stream cache is namespaced. `raw` is ours, not the API's. */
export type Resolution = "raw" | BucketWidth;

/** Finest to coarsest. */
const BUCKET_WIDTHS = Object.keys(BUCKET_WIDTH_MS) as BucketWidth[];

/** Below this much time per pixel, bucketing stops earning its cost — fetch the points instead. */
const RAW_BELOW_MS_PER_PIXEL = 500;

/**
 * Pick the coarsest bucket width ≥ span/plotWidth so points are not denser than pixels.
 * Prefer a width that yields at least 2 points when possible (a line needs ≥2 points).
 * Very short spans fall through to `raw`, which may still plot fewer than 2 points.
 */
export function pickResolution(widthPx: number, startMs: number, endMs: number): Resolution {
    const span = endMs - startMs;
    const msPerPixel = span / Math.max(1, widthPx);

    if (msPerPixel <= RAW_BELOW_MS_PER_PIXEL) return "raw";

    const finestFit = BUCKET_WIDTHS.findIndex((width) => BUCKET_WIDTH_MS[width] >= msPerPixel);
    let chosen = finestFit === -1 ? BUCKET_WIDTHS.length - 1 : finestFit;

    while (span / BUCKET_WIDTH_MS[BUCKET_WIDTHS[chosen]] < 2) {
        if (chosen === 0) return "raw";
        chosen -= 1;
    }

    return BUCKET_WIDTHS[chosen];
}
